import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/TransferDeviceFromOperationToStorage.css';
import CustomButton from '../CustomElements/CustomButton';
import Selector from '../CustomElements/Selector';
import DateSelector from '../CustomElements/DateSelector';
import CustomTextarea from '../CustomElements/CustomTextarea';
import CustomTitle from '../CustomElements/CustomTitle';
import FormContainer from '../CustomElements/FormContainer';
import CustomContainer from '../CustomElements/CustomContainer';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import FilePicker from '../FileManager/FilePicker';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
import useFormDataChanged from '../../customHook/useFormDataChanged';
import useSuccessListener from '../../customHook/useSuccessListener';
import { convertToDate, transformFieldsToMoscowStart } from '../../utils/convertToDate';
// Утилиты
import { getCurrentStatusId } from '../../utils/getCurrentStatusId';
import { handleUniversalChange } from '../../utils/formUtils';
// Действия Redux
import { transferDeviceForStorage } from '../../redux/actions/devices.action';
import { editStorageStatusFromServer } from '../../redux/actions/statuses.action';
import { load } from '../../redux/actions/loader.action';
import { initStatusesFromServer } from '../../redux/actions/statuses.action';
import { omitFields } from '../../utils/omitFields';
import { removeEmptyStrings } from '../../utils/removeEmptyStrings';
import { prepareObjectForSubmit } from '../../utils/prepareObjectForSubmit';

// компонент изменения статуса эксплуатации возврат и  принятия на хранение из эксплуатации

function TransferDeviceFromOperationToStorage({ currentDevice, closeModal, currentStatus, title, isEdit, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();
  // получаем опции из стора
  const optionsForSelect = useSelector((state) => state.options);

  // Состояния для кнопки и уведомления об успешном добавлении
  const [addSuccess, setAddSuccess] = useState(false);

  // состояние  для необязательных полей
  const [optionalFields, setOptionalFields] = useState(['attachmentName', 'acceptanceCertName']);

  // отмена подтверждения
  const closeStatusEditModal = () => {
    setAddSuccess(false);
  };

  // Состояние для формы
  const initialFormData = useMemo(
    () =>
      currentStatus
        ? {
            id: currentStatus?.id,
            deviceId: currentStatus?.deviceId,
            fromStatusId: currentStatus?.fromStatusId,
            date: convertToDate(currentStatus?.date),
            receiverId: currentStatus?.receiverId,
            isBroken: currentStatus?.broken,
            acceptanceCert: currentStatus?.acceptanceCert || '',
            acceptanceCertName: currentStatus?.acceptanceCertName || '',
            storageId: currentStatus?.storageId || '',
            attachment: currentStatus?.attachment || '',
            attachmentName: currentStatus?.attachmentName || '',
            note: currentStatus?.note || '',
          }
        : {
            deviceId: currentDevice.id,
            fromStatusId: getCurrentStatusId(currentDevice?.status, optionsForSelect.deviceStatuses),
            date: '',
            receiverId: '',
            isBroken: '',
            acceptanceCert: '',
            storageId: '',
            attachment: '',
            note: '',
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStatus]
  );
  const [formData, setFormData] = useState(initialFormData);

  // Обновляем formData при изменении currentStatus
  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  // Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, optionalFields);

  //проверяем были ли изменения в форме (true-да, true-нет)
  const isFormEdit = useFormDataChanged(initialFormData, formData, isEdit);

  // Обработчик отправки формы передачи устройства
  const handlerSubmit = (event) => {
    event.preventDefault();
    if (isEdit) {
      // Если редактируем — просто открываем модалку подтверждения
      setAddSuccess(true);
      return;
    }
    // Удаляем лишние поля, не подлежащие отправке
    const formWithoutExcludedFields = omitFields(formData, ['deviceId', 'acceptanceCertName', 'attachmentName']);
    // Удаляем поля с пустыми строками
    const formWithoutEmptyStrings = removeEmptyStrings(formWithoutExcludedFields);
    // Преобразуем указанные поля в начало дня по Москве
    const payload = transformFieldsToMoscowStart(formWithoutEmptyStrings, ['date']);
    dispatch(
      transferDeviceForStorage({
        payload,
        deviceId: formData?.deviceId,
        typeId: currentDevice?.typeId,
        path: 'usage',
      })
    );
  };

  const handlerSubmitEdit = (event) => {
    event.preventDefault();
    //переводим в состояние загрузки
    dispatch(load());
    // сравниваем начальное и текущее состояние заменяем на null пустые строки для удаленных полей
    const formDataWithEmptyStrings = prepareObjectForSubmit(initialFormData, formData);
    // Удаляем поля, которые не нужно отправлять
    const formWithoutExcludedFields = omitFields(formDataWithEmptyStrings, [
      'id',
      'deviceId',
      'acceptanceCertName',
      'attachmentName',
    ]);
    // Преобразуем указанные поля в начало дня по Москве
    const payload = transformFieldsToMoscowStart(formWithoutExcludedFields, ['date']);
    // Отправляем данные на сервер через Redux
    dispatch(
      editStorageStatusFromServer({
        payload,
        deviceId: formData?.deviceId,
        statusId: formData?.id,
        type: currentDevice?.typeId,
        path: 'usage',
      })
    );
  };

  // //отслеживаем выполнение запроса
  useSuccessListener(setFormData, initialFormData, closeModal, closeStatusEditModal);

  // при размонтировании отправляем запрос чтобы получить обновленный стейт
  useEffect(() => {
    return () => {
      dispatch(initStatusesFromServer(currentDevice.id, 'usage'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // обновление массива с необязательными полями
  useEffect(() => {
    setOptionalFields((prevFields) => {
      const fieldsToManage = ['attachment'];
      if (!formData?.isBroken) {
        // Добавляем поля, избегая дубликатов
        return [...new Set([...prevFields, ...fieldsToManage])];
      }
      if (formData?.isBroken) {
        // Удаляем указанные поля, если состояние неисправен
        return prevFields.filter((field) => !fieldsToManage.includes(field));
      }
    });
  }, [formData.isBroken]);

  return (
    <CustomContainer>
      <CustomTitle
        titleText={title || t?.titleAcceptForStorage || 'ПРИНЯТИЕ НА ХРАНЕНИЕ УСТРОЙСТВА'}
        deviceType={currentDevice.type}
        deviceId={currentDevice.id}
      />
      {addSuccess && (
        <ConfirmationPage
          confirmText={t?.statusChangeConfirmationText || 'Вы уверены, что хотите изменить данные статуса?'}
          confirmButtonText={t?.confirmButton || 'Изменить'}
          cancelButtonText={t?.cancelButton || 'Отменить'}
          onConfirm={handlerSubmitEdit} // Функция, которая должна быть вызвана при подтверждении
          onCancel={closeStatusEditModal} // Функция для закрытия модального окна или отмены действия
        />
      )}
      {!addSuccess && (
        <FormContainer>
          <DateSelector
            column="left"
            minDate={convertToDate(currentDevice?.lastActionDate)}
            maxDate={new Date()}
            selectedDate={formData.date}
            setSelectedDate={(date) => handleUniversalChange(formData, setFormData, null, 'date', date, 'date')}
            label={t?.labelReturnDate || 'Дата возврата *'}
            placeholder={t?.placeholderSelectDate || 'Выберите дату'}
            name="date"
            width="100%"
            height="var(--sadr-font-size-60)"
          />
          <Selector
            column="left"
            options={optionsForSelect.employees}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'receiverId', selectedOption, 'select')
            }
            value={optionsForSelect.employees?.find((option) => option.value === formData.receiverId) || ''}
            label={t?.labelReceiverName || 'ФИО принявшего *'}
            placeholder={t?.placeholderReceiverName || 'Выберите ФИО принявшего'}
            name="receiverId"
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
          />
          <FilePicker
            column="left"
            label={t?.labelAcceptanceCert || 'Акт приема-передачи *'}
            formData={formData}
            setFormData={setFormData}
            fieldName="acceptanceCertName"
            fieldIdName="acceptanceCert"
            rowSpan={2}
          />
          <Selector
            column="right"
            options={optionsForSelect.storages}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'storageId', selectedOption, 'select')
            }
            value={optionsForSelect.storages?.find((option) => option.value === formData.storageId) || ''}
            label={t?.labelStorageLocation || 'Место хранения *'}
            placeholder={t?.placeholderStorageLocation || 'Выберите место хранения'}
            name="storageId"
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
          />
          <Selector
            column="right"
            options={optionsForSelect.condition.filter((item) => item.value !== undefined)}
            onChange={(selectedOption) =>
              handleUniversalChange(formData, setFormData, null, 'isBroken', selectedOption, 'select')
            }
            value={optionsForSelect.condition?.find((option) => option.value === formData.isBroken) || ''}
            label={t?.labelCondition || 'Состояние *'}
            placeholder={t?.placeholderCondition || 'Не определено'}
            name="isBroken"
            width="100%"
            height="var(--sadr-font-size-60)"
            isMulti={false}
          />
          <FilePicker
            column={'right'}
            label={`${t?.defectsReportTitle || 'Дефектная ведомость'}${formData?.isBroken ? ' *' : ''}`}
            formData={formData}
            setFormData={setFormData}
            fieldName="attachmentName"
            fieldIdName="attachment"
            rowSpan={2}
          />
          <CustomTextarea
            onChange={(event) => handleUniversalChange(formData, setFormData, event)}
            value={formData.note}
            label={t?.labelComment || 'Комментарий *'}
            placeholder={t?.placeholderComment || 'Оставьте комментарий'}
            name="note"
            type="textArea"
            width="100%"
            columnspan={2}
          />

          <CustomButton
            onClick={(event) => {
              handlerSubmit(event);
            }}
            text={
              isEdit ? t?.buttonSaveChanges || 'Сохранить изменения' : t?.buttonAcceptDevice || 'Принять устройство'
            }
            disabled={isButtonDisabled || !isFormEdit}
            isButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
          />
        </FormContainer>
      )}
    </CustomContainer>
  );
}

export default TransferDeviceFromOperationToStorage;
